var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { ABILITY_COLORS } from '../../api/models';
import { AvatarAbility } from '@common/contracts';
import { getLevelEvents } from '../../api/level-events';
import './ScenarioOverlay.scss';
const ScenarioOverlay = ({ coordinateString, nodeData, cellSize, overlayOpacity, levelId, events, displayOptions }) => {
    const [scenarioInfos, setScenarioInfos] = useState([]);
    const [loading, setLoading] = useState(false);
    // Build this node's scenario info from a set of events (shared helper).
    const buildFrom = (allEvents) => {
        const nodeScenarios = [];
        for (const simpleEvent of nodeData.simpleEvents || []) {
            const fullEvent = allEvents.find(event => event.id === simpleEvent.eventId);
            if (fullEvent && fullEvent.encounters && fullEvent.encounters.length > 0) {
                nodeScenarios.push({ eventId: fullEvent.id, eventTitle: fullEvent.title, encounters: fullEvent.encounters });
            }
        }
        return nodeScenarios;
    };
    // Load event details for this node's simple events. When the editor passes live
    // `events`, read them directly so stamp/edit changes render instantly; otherwise
    // fall back to fetching once (e.g. contexts that don't thread events down).
    useEffect(() => {
        if (!nodeData.simpleEvents || nodeData.simpleEvents.length === 0) {
            setScenarioInfos([]);
            return;
        }
        if (events) {
            setScenarioInfos(buildFrom(events));
            return;
        }
        const loadEventDetails = () => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            try {
                setScenarioInfos(buildFrom(yield getLevelEvents(levelId)));
            }
            catch (error) {
                console.error(`Failed to load events for node ${coordinateString}:`, error);
                setScenarioInfos([]);
            }
            finally {
                setLoading(false);
            }
        });
        loadEventDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeData.simpleEvents, levelId, coordinateString, events]);
    // Don't render if no scenarios or still loading
    if (loading || scenarioInfos.length === 0) {
        return null;
    }
    // Nothing toggled on → render nothing. Otherwise the overlay draws empty param
    // plates (e.g. when the Scenarios overlay is left on with every toggle off,
    // such as after the Balance stamp restores the view on close).
    const hasVisibleContent = displayOptions.showAbility || displayOptions.showCost
        || displayOptions.showDifficulty || displayOptions.showReward || displayOptions.showPenalty;
    if (!hasVisibleContent) {
        return null;
    }
    // Calculate if cell is large enough to show details with adaptive parameters
    const showDetails = cellSize >= 100; // Lowered the threshold for the horizontal layout
    const showCompact = cellSize >= 60 && cellSize < 100;
    const showMinimal = cellSize >= 30 && cellSize < 60;
    // Adaptive logic for parameters depending on the cell size
    const getVisibleParams = (cellSize) => {
        if (cellSize >= 140) {
            // Show all parameters including cost
            return { showCost: true, showDiff: true, showRew: true, showPen: true };
        }
        else if (cellSize >= 110) {
            // Show the three most important parameters
            return { showCost: false, showDiff: true, showRew: true, showPen: true };
        }
        else if (cellSize >= 85) {
            // Show the two most important parameters
            return { showCost: false, showDiff: true, showRew: true, showPen: false };
        }
        else if (cellSize >= 70) {
            // Show only the most important one
            return { showCost: false, showDiff: true, showRew: false, showPen: false };
        }
        return { showCost: true, showDiff: true, showRew: true, showPen: true }; // fallback
    };
    const visibleParams = getVisibleParams(cellSize);
    // Get full ability name
    const getAbilityName = (ability) => {
        return ability; // Already the full name
    };
    // Get ability abbreviation for compact mode
    const getAbilityAbbreviation = (ability) => {
        switch (ability) {
            case AvatarAbility.Strength: return 'Str';
            case AvatarAbility.Agility: return 'Agi';
            case AvatarAbility.Perception: return 'Per';
            case AvatarAbility.Intellect: return 'Int';
            case AvatarAbility.Defence: return 'Def';
            default: return '?';
        }
    };
    // Get single letter for minimal mode
    const getAbilityLetter = (ability) => {
        switch (ability) {
            case AvatarAbility.Strength: return 'S';
            case AvatarAbility.Agility: return 'A';
            case AvatarAbility.Perception: return 'P';
            case AvatarAbility.Intellect: return 'I';
            case AvatarAbility.Defence: return 'D';
            default: return '?';
        }
    };
    // Render compact version for smaller cells
    if (showCompact) {
        return (_jsx("div", { className: "scenario-overlay compact", style: { opacity: overlayOpacity }, children: scenarioInfos.map((scenario, _) => (_jsx("div", { className: "scenario-compact", children: scenario.encounters.map((encounter, encounterIndex) => (_jsxs("div", { className: "encounter-compact", children: [displayOptions.showAbility && (_jsx("div", { className: "ability-name-compact", style: {
                                backgroundColor: ABILITY_COLORS[encounter.ability],
                                color: '#fff'
                            }, children: getAbilityAbbreviation(encounter.ability) })), _jsxs("div", { className: "params-compact", children: [displayOptions.showCost && (_jsx("div", { className: "param-compact cost", children: _jsx("span", { className: "value", children: encounter.powerCost }) })), displayOptions.showDifficulty && (_jsx("div", { className: "param-compact difficulty", children: _jsx("span", { className: "value", children: encounter.difficulty }) })), displayOptions.showReward && (_jsx("div", { className: "param-compact reward", children: _jsx("span", { className: "value", children: encounter.rewardAmount }) })), displayOptions.showPenalty && (_jsx("div", { className: "param-compact penalty", children: _jsx("span", { className: "value", children: encounter.failurePenalty }) }))] })] }, `${scenario.eventId}-${encounterIndex}`))) }, scenario.eventId))) }));
    }
    // Render minimal version for very small cells
    if (showMinimal) {
        // Show only ability indicators with colors
        const allAbilities = new Set();
        scenarioInfos.forEach(scenario => {
            scenario.encounters.forEach(encounter => {
                allAbilities.add(encounter.ability);
            });
        });
        return (_jsx("div", { className: "scenario-overlay minimal-abilities", style: { opacity: overlayOpacity }, children: displayOptions.showAbility ? (_jsx("div", { className: "abilities-indicators", children: Array.from(allAbilities).map(ability => (_jsx("div", { className: "ability-indicator", style: {
                        backgroundColor: ABILITY_COLORS[ability],
                        color: '#fff'
                    }, children: getAbilityLetter(ability) }, ability))) })) : (_jsx("div", { className: "abilities-indicators", children: _jsx("div", { className: "ability-indicator disabled", children: "?" }) })) }));
    }
    // Render detailed version for larger cells
    if (showDetails) {
        return (_jsx("div", { className: "scenario-overlay detailed", style: { opacity: overlayOpacity }, children: scenarioInfos.map((scenario, _) => (_jsx("div", { className: "scenario-info", children: scenario.encounters.map((encounter, encounterIndex) => (_jsxs("div", { className: "encounter-info", children: [displayOptions.showAbility && (_jsx("div", { className: "ability-header", style: {
                                backgroundColor: ABILITY_COLORS[encounter.ability],
                                color: '#fff'
                            }, children: getAbilityName(encounter.ability) })), _jsxs("div", { className: "parameters", children: [displayOptions.showCost && visibleParams.showCost && (_jsx("div", { className: "parameter cost-param", children: _jsx("span", { className: "param-value", children: encounter.powerCost }) })), displayOptions.showDifficulty && visibleParams.showDiff && (_jsx("div", { className: "parameter difficulty-param", children: _jsx("span", { className: "param-value", children: encounter.difficulty }) })), displayOptions.showReward && visibleParams.showRew && (_jsx("div", { className: "parameter reward-param", children: _jsx("span", { className: "param-value", children: _jsx("span", { className: "value-number", children: encounter.rewardAmount }) }) })), displayOptions.showPenalty && visibleParams.showPen && (_jsx("div", { className: "parameter penalty-param", children: _jsx("span", { className: "param-value", children: encounter.failurePenalty }) }))] })] }, `${scenario.eventId}-${encounterIndex}`))) }, scenario.eventId))) }));
    }
    // For very small cells, show just a simple dot indicator
    return (_jsx("div", { className: "scenario-overlay minimal", style: { opacity: overlayOpacity }, children: _jsx("div", { className: "scenario-dot" }) }));
};
export default ScenarioOverlay;
